import React from 'react';
import styled from 'styled-components';
import { LucideIcon } from 'lucide-react';

interface StatusCardProps {
  title: string;
  mainValue: string | number;
  subValue?: string;
  color: string;
  icon: LucideIcon;
  subtitle?: string;
  onClick?: () => void;
}

const Card = styled.div<{ $color: string; $clickable: boolean }>`
  padding: 14px 16px;
  border-right: 2px solid ${props => props.$color};
  transition: all 0.2s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    ${props => props.$clickable && `
      background: var(--hover-background);
    `}
  }

  @media (max-width: 767px) {
    padding: 12px 14px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardIconWrapper = styled.div<{ $color: string }>`
  color: ${props => props.$color};
  opacity: 0.7;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MainValue = styled.div<{ $color: string }>`
  font-size: 22px;
  font-weight: 600;
  color: ${props => props.$color};
  line-height: 1.2;

  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const SubValue = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const Subtitle = styled.div<{ $color: string }>`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$color};
  margin-top: 2px;
`;

const StatusCard: React.FC<StatusCardProps> = ({
  title,
  mainValue,
  subValue,
  color,
  icon: Icon,
  subtitle,
  onClick
}) => {
  return (
    <Card $color={color} $clickable={!!onClick} onClick={onClick}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardIconWrapper $color={color}>
          <Icon size={20} strokeWidth={1.5} />
        </CardIconWrapper>
      </CardHeader>
      <CardBody>
        <MainValue $color={color}>{mainValue}</MainValue>
        {subValue && <SubValue>{subValue}</SubValue>}
        {subtitle && <Subtitle $color={color}>{subtitle}</Subtitle>}
      </CardBody>
    </Card>
  );
};

export default StatusCard;
